// 📁 src/lightversion/RegistrationGateLite.jsx

import React, { useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLite } from "./context/LiteContext";
import LiteDayGate from "./LiteDayGate";

const RegistrationGateLite = () => {
  const navigate = useNavigate();
  const { 
  liteJourney,
  entryDecided,
  loading: liteLoading
  } = useLite();

  const purpose = liteJourney?.purpose?.statement;
  const direction = liteJourney?.direction;

  // --------------------------------------------------
  // 🔐 Resume marker (CRITICAL — UNCHANGED)
  // --------------------------------------------------
  useEffect(() => {
    localStorage.setItem("lite_resume_step", "market-potential");
    console.log("🔐 Lite resume step set → market-potential");
  }, []);

  /* =====================================================
   🔒 ENTRY DECISION GUARD
===================================================== */
useEffect(() => {

  if (liteLoading) return;

  if (!entryDecided && window.location.pathname !== "/lite/welcome") {
    navigate("/lite/welcome",{replace:true});
  }

  }, [entryDecided, liteLoading, navigate]);

  // --------------------------------------------------
  // 🛡️ Safety guard — accidental entry protection
  // --------------------------------------------------
  useEffect(() => {
    if (!purpose && !liteLoading) {
      console.warn("⚠️ RegistrationGateLite: Missing purpose → redirecting");
      navigate("/lite/purpose", { replace: true });
    }
    }, [purpose, liteLoading, navigate]);

  return (
    <LiteDayGate allow={["CHECKPOINT", "SAVE_PROGRESS", "PAUSE_AND_RETURN"]}>
      <Box sx={{ maxWidth: 800, mx: "auto", p: 4 }}>
        {/* ---------------- HEADER ---------------- */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
            Before you go further…
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 4 }}>
            You’ve uncovered something meaningful.  
            Let’s make sure this clarity stays with you.
          </Typography>
        </motion.div>

        {/* ---------------- CONTEXT SUMMARY ---------------- */}
        <Stack spacing={3}>
          {/* Purpose */}
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Your purpose
              </Typography>
              <Typography sx={{ fontStyle: "italic", mt: 1 }}>
                “{purpose}”
              </Typography>
            </CardContent>
          </Card>

          {/* Direction */}
          {direction && (
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Direction you’re exploring
                </Typography>

                <Typography fontWeight="bold" sx={{ mt: 1 }}>
                  {direction.title}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {direction.summary}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Stack>

        <Divider sx={{ my: 4 }} />

        {/* ---------------- REASSURANCE ---------------- */}
        <Card sx={{ mb: 4, backgroundColor: "#f9fafb" }}>
          <CardContent>
            <Typography fontWeight="bold" sx={{ mb: 1 }}>
              Why create a free account?
            </Typography>

            <ul style={{ marginTop: 8, paddingLeft: 18 }}>
              <li>Save your thinking and reflections</li>
              <li>Resume exactly where you left off</li>
              <li>Unlock a grounded view of real market potential</li>
            </ul>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 2, display: "block" }}
            >
              No spam. No pressure. No payment required.
            </Typography>
          </CardContent>
        </Card>

        {/* ---------------- PRIMARY CTA ---------------- */}
        <Stack spacing={1.5}>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/register?source=lite")}
          >
            Save my progress & continue →
          </Button>

          <Button
            variant="text"
            color="inherit"
            onClick={() => navigate("/")}
          >
            I’ll come back later
          </Button>

          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
          >
            We’ll bring you back right here when you return.
          </Typography>
        </Stack>

        {/* -------------------------------------------------------
           💤 PAUSE STATE (NO DAY MUTATION)
        ------------------------------------------------------- */}
        <Box textAlign="center" sx={{ mt: 6 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            Your progress is safe. You can return anytime to continue Day 5.
          </Typography>
        </Box>
      </Box>
    </LiteDayGate>
  );
};

export default RegistrationGateLite;
